/**
 * Bridge between the service-worker lifecycle (plain TS, runs in main.tsx at
 * module scope, outside React) and <AppUpdateToast>, the React UI that asks
 * for update consent.
 *
 * main.tsx calls notifyServiceWorkerUpdate() when a waiting worker appears;
 * the toast component subscribes and shows the consent toast. The activate
 * callback captures everything main.tsx needs (SKIP_WAITING + reload), so
 * this module stays free of service-worker knowledge.
 */
export type ActivateUpdate = () => void

const listeners = new Set<(activate: ActivateUpdate) => void>()
let latest: ActivateUpdate | null = null

/** Announce a waiting worker. A newer announcement replaces the previous one. */
export function notifyServiceWorkerUpdate(activate: ActivateUpdate): void {
  latest = activate
  for (const listener of listeners) listener(activate)
}

/**
 * Subscribe to update announcements. Fires immediately with the latest
 * announcement if one happened before subscription (component mounted after
 * the update was found). Returns an unsubscribe function.
 */
export function subscribeToServiceWorkerUpdates(
  listener: (activate: ActivateUpdate) => void,
): () => void {
  listeners.add(listener)
  if (latest) listener(latest)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam: clear the replayed-latest announcement between tests. */
export function __resetServiceWorkerUpdatesForTests(): void {
  latest = null
}
